import type { AttachmentNote } from "../types";

// An image pasted into a note is stored twice on purpose. LCCRM-87.
//
// In the text, as a markdown image line at the spot it was pasted, which is what
// puts the picture between the right two paragraphs when the note is rendered.
// And in the note's attachments, which is what the storage cleanup reads: the
// file is deleted with the note, or when the attachment is removed.
//
// These helpers keep the two in step: an attachment already shown inline is not
// repeated in the attachment strip, and one-line previews drop the markdown line
// instead of printing it.

// ![alt](src) and ![alt](src "title"), the src optionally in angle brackets.
const IMAGE_MARKDOWN = /!\[[^\]]*\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/g;

/** The line that shows `src` as a picture when the note is rendered. */
export const imageMarkdown = (src: string, alt = "image") =>
  `![${alt.replace(/[[\]]/g, "")}](${src})`;

/**
 * What sits at the cursor while the file uploads. Deliberately not image
 * markdown: if it is ever saved as it is, it reads as a plain sentence instead
 * of rendering a broken picture.
 */
export const uploadPlaceholder = (id: number | string) =>
  `[Uploading image ${id}…]`;

/**
 * The images a paste carries, when the paste is an image and nothing else.
 *
 * Text wins: Excel, Word and PowerPoint put a rendered picture of the selection
 * on the clipboard beside its text, and pasting cells must not insert a
 * screenshot of them. A screenshot, or an image copied from a web page, has no
 * plain text flavour.
 */
export const pastedImages = (
  clipboard: Pick<DataTransfer, "files" | "getData">,
): File[] => {
  if (clipboard.getData("text/plain").trim() !== "") return [];
  return Array.from(clipboard.files).filter((file) =>
    file.type.startsWith("image/"),
  );
};

/** Every source the text shows as an inline image. */
export const inlineImageSources = (
  text: string | null | undefined,
): Set<string> => {
  const sources = new Set<string>();
  for (const match of (text ?? "").matchAll(IMAGE_MARKDOWN)) {
    if (match[1]) sources.add(match[1]);
  }
  return sources;
};

/** The attachments the text does not already show: the strip under a note. */
export const attachmentsNotInline = <T extends Pick<AttachmentNote, "src">>(
  text: string | null | undefined,
  attachments: T[] | null | undefined,
): T[] => {
  const inline = inlineImageSources(text);
  return (attachments ?? []).filter(
    (attachment) => !inline.has(attachment.src),
  );
};

/** The attachments the text shows inline: the thumbnails under the note box. */
export const attachmentsInline = <T extends Pick<AttachmentNote, "src">>(
  text: string | null | undefined,
  attachments: T[] | null | undefined,
): T[] => {
  const inline = inlineImageSources(text);
  return (attachments ?? []).filter((attachment) => inline.has(attachment.src));
};

/** The text for a one-line preview: no image lines, no gap where one was. */
export const stripInlineImages = (text: string | null | undefined): string =>
  (text ?? "")
    .replace(IMAGE_MARKDOWN, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
